import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { launchesApi, templatesApi, typesApi } from "../api/endpoints";
import { useSettings } from "../hooks/useSettings";
import { IcoClose, IcoPlus, IcoSearch, IcoTemplate } from "../components/icons";
import type { Stage, Template } from "../types/domain";
import { TemplateRow } from "./templates/TemplateRow";
import { TemplateEditor } from "./templates/TemplateEditor";
import { CanWrite } from "../components/CanWrite";

const STAGE_FILTERS: Array<{ id: Stage; label: string; color: string }> = [
  { id: "warmup", label: "Прогрев", color: "#3B82F6" },
  { id: "sales", label: "Продажи", color: "#14B8A6" },
  { id: "close", label: "Дожим", color: "#F59E0B" },
];

type SortKey = "created" | "usage" | "recent" | "az";

const SORT_OPTIONS: Array<{ id: SortKey; label: string }> = [
  { id: "created", label: "Сначала новые" },
  { id: "usage", label: "По использованию" },
  { id: "recent", label: "Недавние применения" },
  { id: "az", label: "А→Я" },
];

export function TemplatesPage() {
  const qc = useQueryClient();
  const { settings } = useSettings();
  const activeLaunchId = settings?.activeLaunchId ?? null;

  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState<Stage | "all">("all");
  const [tagFilter, setTagFilter] = useState<string>("all");
  const [sort, setSort] = useState<SortKey>("created");
  const [editorTpl, setEditorTpl] = useState<Template | null | undefined>(undefined);
  // undefined = closed; null = creating new; Template = editing

  const templatesQuery = useQuery({
    queryKey: ["templates"],
    queryFn: async () => (await templatesApi.list()).templates,
  });
  const typesQuery = useQuery({
    queryKey: ["types"],
    queryFn: async () => (await typesApi.list()).types,
  });
  const launchesQuery = useQuery({
    queryKey: ["launches"],
    queryFn: async () => (await launchesApi.list()).launches,
  });

  const templates = templatesQuery.data ?? [];
  const types = typesQuery.data ?? [];
  const launches = launchesQuery.data ?? [];

  const allTags = useMemo(() => {
    const set = new Set<string>();
    templates.forEach((t) => (t.tags ?? []).forEach((x) => set.add(x)));
    return Array.from(set).sort();
  }, [templates]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = templates.filter((t) => {
      if (stageFilter !== "all" && t.stage !== stageFilter) return false;
      if (tagFilter !== "all" && !(t.tags ?? []).includes(tagFilter)) return false;
      if (q) {
        const hay = `${t.name} ${t.subject ?? ""} ${t.preview ?? ""} ${(t.tags ?? []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const byCreatedDesc = (a: Template, b: Template) =>
      (b.createdAt ?? "").localeCompare(a.createdAt ?? "");
    list.sort((a, b) => {
      if (sort === "az") return a.name.localeCompare(b.name, "ru");
      if (sort === "recent") {
        const diff = (b.lastUsedAt ?? "").localeCompare(a.lastUsedAt ?? "");
        return diff !== 0 ? diff : byCreatedDesc(a, b);
      }
      if (sort === "usage") {
        const diff = (b.usageCount ?? 0) - (a.usageCount ?? 0);
        return diff !== 0 ? diff : byCreatedDesc(a, b);
      }
      return byCreatedDesc(a, b);
    });
    return list;
  }, [templates, query, stageFilter, tagFilter, sort]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["templates"] });

  const duplicateMutation = useMutation({
    mutationFn: (tpl: Template) =>
      templatesApi.create({
        name: `Копия — ${tpl.name}`,
        typeId: tpl.typeId,
        stage: tpl.stage,
        subject: tpl.subject,
        preview: tpl.preview,
        time: tpl.time ?? null,
        tags: tpl.tags ?? [],
      }),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => templatesApi.remove(id),
    onSuccess: invalidate,
  });

  const handleDelete = (tpl: Template) => {
    if (window.confirm(`Удалить шаблон «${tpl.name}»?`)) {
      deleteMutation.mutate(tpl.id);
    }
  };

  const filtersDirty = query !== "" || stageFilter !== "all" || tagFilter !== "all";

  return (
    <div className="launches-page templates-page">
      <div className="launches-head">
        <div>
          <h1 className="launches-title">Шаблоны писем</h1>
          <div className="launches-subtitle">
            Заготовки для быстрого создания карточек. <b>{templates.length}</b>{" "}
            {pluralTemplates(templates.length)} · применяйте через кнопку «Применить».
          </div>
        </div>
        <div className="launches-head-right">
          <div className="search" style={{ minWidth: 240 }}>
            <IcoSearch size={14} stroke="#7A849B" />
            <input
              placeholder="Поиск по теме, тексту, тегам…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <CanWrite>
            <button type="button" className="btn-primary" onClick={() => setEditorTpl(null)}>
              <IcoPlus size={14} stroke="white" sw={2.5} /> Новый шаблон
            </button>
          </CanWrite>
        </div>
      </div>

      <div className="templates-filters">
        <div className="templates-filter-group">
          <span className="filter-label">Стадия:</span>
          <div className="seg seg-inline">
            <button
              type="button"
              className={stageFilter === "all" ? "active" : ""}
              onClick={() => setStageFilter("all")}
            >
              Все
            </button>
            {STAGE_FILTERS.map((s) => (
              <button
                type="button"
                key={s.id}
                className={stageFilter === s.id ? "active" : ""}
                onClick={() => setStageFilter(s.id)}
              >
                <span className="stage-dot" style={{ background: s.color }} />
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {allTags.length > 0 && (
          <div className="templates-filter-group">
            <span className="filter-label">Тег:</span>
            <select
              className="form-input mini"
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
            >
              <option value="all">Все теги</option>
              {allTags.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="templates-filter-group" style={{ marginLeft: "auto" }}>
          <span className="filter-label">Сортировать:</span>
          <div className="seg seg-inline">
            {SORT_OPTIONS.map((s) => (
              <button
                type="button"
                key={s.id}
                className={sort === s.id ? "active" : ""}
                onClick={() => setSort(s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {filtersDirty && (
          <button
            type="button"
            className="filter-chip"
            onClick={() => {
              setQuery("");
              setStageFilter("all");
              setTagFilter("all");
            }}
            style={{ borderStyle: "dashed" }}
          >
            <IcoClose size={11} /> Сбросить
          </button>
        )}
      </div>

      {templatesQuery.isLoading ? (
        <div className="launches-empty">
          <p>Загружаем…</p>
        </div>
      ) : templatesQuery.isError ? (
        <div className="launches-empty">
          <h3>Не удалось загрузить шаблоны</h3>
          <p>{(templatesQuery.error as Error).message}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="launches-empty">
          <div className="launches-empty-icon">
            <IcoTemplate size={24} stroke="var(--text-muted)" />
          </div>
          <h3>Ничего не найдено</h3>
          <p>{templates.length === 0 ? "Создайте первый шаблон." : "Попробуйте другой запрос или снимите фильтры."}</p>
        </div>
      ) : (
        <div className="templates-list">
          <div className="templates-list-head">
            <div></div>
            <div>Название · тема</div>
            <div>Стадия</div>
            <div>Теги</div>
            <div>Использовано</div>
            <div></div>
          </div>
          {filtered.map((t) => (
            <TemplateRow
              key={t.id}
              template={t}
              type={types.find((x) => x.id === t.typeId)}
              launches={launches}
              activeLaunchId={activeLaunchId}
              onEdit={() => setEditorTpl(t)}
              onDuplicate={() => duplicateMutation.mutate(t)}
              onDelete={() => handleDelete(t)}
            />
          ))}
        </div>
      )}

      {editorTpl !== undefined && (
        <TemplateEditor template={editorTpl} types={types} onClose={() => setEditorTpl(undefined)} />
      )}
    </div>
  );
}

function pluralTemplates(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return "шаблон";
  if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)) return "шаблона";
  return "шаблонов";
}
